import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { usePeriod, periodLabel } from '@/app/PeriodContext';
import { formatMoney, formatAmount, formatDate } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { SettleDialog } from '@/features/transactions/SettleDialog';
import { PayFromObligations } from './PayFromObligations';
import type { Transaction, ReferenceData } from '@/features/transactions/types';

type Item = {
  id: number;
  /**
   * De onde a linha vem — e por que isso importa.
   *
   * `transaction` é um lançamento comum: baixa pelo caminho normal.
   * `loan_installment` é parcela de dívida, que não existe como lançamento até
   * ser paga. Baixá-la pelo caminho do lançamento daria baixa no registro
   * errado, porque os `id` são de tabelas diferentes.
   */
  kind: 'transaction' | 'loan_installment' | 'card_invoice';
  loan_id: number | null;
  /** Só na fatura: leva para a tela de Cartões, onde ela se paga. */
  card_id: number | null;
  /**
   * Quanto DESTA linha é essencial — só a fatura preenche.
   *
   * Uma fatura não é essencial nem supérflua por inteiro: dentro dela há
   * farmácia e há restaurante. Um selo binário mentiria dos dois lados, e um
   * selo que discordasse do bloco "dos próximos 30 dias" seria pior ainda.
   */
  essential_amount: MoneyString | null;
  description: string;
  due_date: string;
  remaining_amount: MoneyString;
  party_name: string | null;
  category_name: string | null;
  entity_name: string | null;
  priority: string | null;
  is_essential: boolean;
  is_overdue: boolean;
  days_overdue: number;
};

type Bucket = { date: string | null; total: MoneyString; count: number; items: Item[] };

type Summary = {
  reference_date: string;
  /** Qual contraparte está filtrando, devolvido pelo servidor. */
  filtered_party_id: number | null;
  /** Recorte por data de vencimento — vem do período global. */
  period: { start: string | null; end: string };
  totals: {
    open: MoneyString;
    open_count: number;
    overdue: MoneyString;
    overdue_count: number;
    due_today: MoneyString;
    next_7_days: MoneyString;
    next_30_days: MoneyString;
    /** O número da tela: o que vence dentro do período. */
    in_period: MoneyString;
    in_period_count: number;
  };
  aging: Array<{ label: string; is_overdue: boolean; count: number; total: MoneyString }>;
  upcoming: Record<string, Bucket>;
  by_party: Array<{ party_id: number | null; party_name: string; total: MoneyString; count: number }>;
  /**
   * De onde vem cada real que se deve — as três origens, agrupadas.
   *
   * Sucede `by_party` no seletor. Aquele via só os lançamentos: numa base real
   * media 62% do que a tela mostrava, porque parcelas de dívida e faturas de
   * cartão nunca estiveram nele. Somar todos os nomes não chegava no total, e
   * nada indicava isso — um seletor incompleto é pior que nenhum, porque
   * parece completo.
   */
  /**
   * O total da LISTA de origens — e não `totals.open`.
   *
   * As duas coisas usam janelas diferentes: `totals.open` é a posição de hoje
   * em todas as datas; a lista é vencido + o que vence no recorte. Usar um no
   * rótulo do outro fazia "Todas — R$ 189.877,31" encabeçar uma lista que
   * somava R$ 12.803,95.
   */
  by_source_total: MoneyString;
  by_source: Array<{
    group: string;
    source: 'card' | 'loan' | 'party' | 'none';
    source_id: number | null;
    name: string;
    total: MoneyString;
    count: number;
  }>;
  essential_split?: { essential: MoneyString; non_essential: MoneyString; critical_priority: MoneyString };
  receivable_metrics?: {
    average_delay_days: number | null;
    settlements_analyzed: number;
    default_rate_percent: number;
    overdue_total: MoneyString;
  };
};

export function ObligationsPage({
  kind,
  selectedIds,
  onOpenAccounts,
  onOpenDebts,
  onOpenCards,
}: {
  kind: 'payables' | 'receivables';
  selectedIds: number[];
  /** Leva à tela de Contas quando a entidade do título não tem nenhuma. */
  onOpenAccounts?: () => void;
  /** Leva à tela de Dívidas: a baixa de parcela acontece lá. */
  onOpenDebts?: () => void;
  /** Leva para Cartões, onde a fatura se paga. */
  onOpenCards?: () => void;
}) {
  const { period } = usePeriod();
  const [settling, setSettling] = useState<Transaction | null>(null);
  /**
   * A origem escolhida, como "card:3" ou "party:17".
   *
   * Um valor só em vez de dois estados separados: com `source` e `sourceId`
   * soltos, existiria o instante em que um mudou e o outro não — e a consulta
   * sairia com um par inválido.
   */
  const [origin, setOrigin] = useState<string>('');

  /** Fatura ou parcela sendo paga aqui mesmo — ver `PayFromObligations`. */
  const [payingOther, setPayingOther] = useState<Item | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const isPayable = kind === 'payables';

  /**
   * O FILTRO É DO SERVIDOR, e não da tela.
   *
   * Filtrar a lista aqui seria mais rápido e produziria o defeito que mais
   * apareceu nesta base: os totais vêm calculados do servidor e continuariam
   * mostrando o valor de todo mundo, ao lado de uma lista já filtrada. Número
   * grande em cima que não bate com a soma de baixo.
   *
   * E somar de novo no cliente para "consertar" seria pior ainda — ADR-0003:
   * `0.1 + 0.2 !== 0.3`, e num sistema financeiro isso vira centavo que
   * ninguém rastreia.
   */
  const summary = useQuery({
    queryKey: [kind, selectedIds, period.from, period.to, origin],
    queryFn: () =>
      api.get<{ data: Summary }>(
        `/${kind}?from=${period.from}&to=${period.to}${
          origin ? `&source=${origin.split(':')[0]}&source_id=${origin.split(':')[1]}` : ''
        }`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
    // Mantém os números anteriores na tela enquanto a nova consulta corre: sem
    // isto, cada troca de filtro pisca a tela inteira para o esqueleto.
    placeholderData: (anterior) => anterior,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  /**
   * Excluir daqui, e não só em Lançamentos.
   *
   * Título criado por engano é descoberto justamente nesta tela, olhando o que
   * se deve. Obrigar a ir até Lançamentos para apagá-lo é fazer o usuário
   * procurar de novo o que ele já tem na frente.
   */
  async function confirmRemove(item: Item) {
    const label = `${item.description} — ${formatMoney(item.remaining_amount)} (venc. ${formatDate(item.due_date)})`;

    if (!confirm(`Excluir o título?

${label}`)) return;

    /**
     * QUEM DECIDE SE PRECISA DE MOTIVO É A API, não esta tela.
     *
     * Título com baixa registrada exige justificativa — já houve dinheiro
     * movimentado, e apagar em silêncio deixaria a baixa órfã. Mas a lista não
     * carrega as baixas, e adivinhar aqui criaria uma segunda regra que
     * divergiria da primeira no dia em que uma das duas mudasse.
     *
     * Então: tenta sem motivo; se a API recusar por falta dele, pergunta e
     * repete. A regra continua existindo num lugar só.
     */
    try {
      await removeTransaction(item.id);
    } catch (error) {
      const needsReason = error instanceof ApiError && error.errors?.reason !== undefined;

      if (!needsReason) {
        alert(error instanceof ApiError ? error.message : 'Não foi possível excluir.');
        return;
      }

      const reason = prompt(
        'Este título já tem baixa registrada. Informe o motivo da exclusão (mínimo 5 caracteres):',
      );

      if (reason === null || reason.trim().length < 5) return;

      try {
        await removeTransaction(item.id, reason.trim());
      } catch (retry) {
        alert(retry instanceof ApiError ? retry.message : 'Não foi possível excluir.');
      }
    }
  }

  async function removeTransaction(id: number, reason?: string) {
    await api.delete(`/transactions/${id}`, {
      entityIds: selectedIds,
      body: reason ? { reason } : undefined,
    });

    void queryClient.invalidateQueries({ queryKey: [kind] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['contracts'] });
  }

  const classify = useMutation({
    mutationFn: ({ id, changes }: { id: number; changes: Record<string, unknown> }) =>
      api.patch(`/obligations/${id}/classify`, changes, { entityIds: selectedIds }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [kind] }),
  });

  async function openSettle(id: number) {
    const res = await api.get<{ data: Transaction }>(`/transactions/${id}`, { entityIds: selectedIds });
    setSettling(res.data);
  }

  if (summary.isPending) return <Skeleton />;
  if (summary.isError) {
    return (
      <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        Não foi possível carregar.
      </p>
    );
  }

  const d = summary.data;
  const buckets = Object.entries(d.upcoming);
  const overdueBucket = buckets.find(([k]) => k === 'vencidos')?.[1];
  const dated = buckets.filter(([k]) => k !== 'vencidos').sort(([a], [b]) => a.localeCompare(b));

  const selectedTotal = dated
    .flatMap(([, b]) => b.items)
    .concat(overdueBucket?.items ?? [])
    .filter((i) => selected.includes(i.id))
    .reduce((s, i) => s + Number(i.remaining_amount), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">
          {isPayable ? 'Contas a pagar' : 'Contas a receber'}
        </h1>

        {/*
          ================================================================
          DE ONDE VEM O PAGAMENTO — um seletor, três origens
          ================================================================

          A alternativa seria um filtro por cartão, outro por dívida, outro por
          fornecedor. Cinco seletores lado a lado que ninguém preenche — e que
          deixariam a mesma dúvida de pé: "filtrei por fornecedor, e o resto
          veio de onde?".

          Agrupada por tipo, a lista responde a pergunta sem filtrar nada:
          basta abrir e ler. E a soma das linhas fecha com o total da tela, o
          que é justamente o que o seletor anterior não fazia.

          `by_source` é o único bloco que NÃO segue o filtro, de propósito: é
          dele que se escolhe. Filtrado, mostraria só o que já está
          selecionado, e não haveria como trocar nem voltar.
        */}
        {(d?.by_source.length ?? 0) > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">Origem</span>
            <select
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              className="input max-w-xs py-1.5 text-sm"
            >
              {/*
                O valor ao lado do nome é o MESMO que a tela mostra ao
                selecionar — vencido mais o que vence no recorte —, e não a
                carteira inteira em aberto. Um seletor dizendo R$ 95.000 e uma
                tela dizendo R$ 8.000 logo depois do clique faz duvidar dos
                dois números.
              */}
              <option value="">Todas — {formatMoney(d?.by_source_total ?? '0')}</option>
              {['Cartões', 'Dívidas', 'Fornecedores'].map((grupo) => {
                /*
                  Sem filtrar `source_id`: a linha "Sem fornecedor" precisa
                  aparecer. Escondê-la tirava R$ 2.568,28 da lista sem nada
                  indicando — o mesmo defeito que esta lista existe para
                  corrigir, repetido dentro dela.
                */
                const itens = (d?.by_source ?? []).filter((o) => o.group === grupo);

                if (itens.length === 0) return null;

                return (
                  <optgroup key={grupo} label={grupo}>
                    {itens.map((o) => (
                      <option key={`${o.source}-${o.source_id}`} value={`${o.source}:${o.source_id}`}>
                        {o.name} — {formatMoney(o.total)}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </label>
        )}
      </div>

      {/* Sem esta linha, o número ao lado de cada nome não diz de quando é. */}
      {(d?.by_source.length ?? 0) > 0 && (
        <p className="mt-1 text-xs text-slate-400">
          Os valores ao lado de cada nome somam o vencido mais o que vence em{' '}
          {periodLabel(period).toLowerCase()}.
        </p>
      )}

      {/*
        Filtro ativo precisa se anunciar.

        Todo número da tela passa a ser sobre uma contraparte só. Sem um aviso
        explícito, alguém que voltou à tela depois de um tempo lê "R$ 3.500 a
        pagar" achando que é o total — e é o de um fornecedor.
      */}
      {origin !== '' && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <span>
            Mostrando apenas{' '}
            <strong>
              {d?.by_source.find((o) => `${o.source}:${o.source_id}` === origin)?.name ?? 'esta origem'}
            </strong>
            . Todos os valores abaixo se referem só a essa origem.
          </span>
          <button
            onClick={() => setOrigin('')}
            className="ml-auto rounded-lg border border-indigo-300 px-2.5 py-1 text-xs transition hover:bg-white dark:border-indigo-800 dark:hover:bg-slate-900"
          >
            Limpar filtro
          </button>
        </div>
      )}

      {/* ================================================================
          DOIS GRUPOS, SEPARADOS NA TELA — não só na legenda
          ================================================================

          Esta tela tem números de duas naturezas, e misturá-los numa fileira
          só fez o usuário desconfiar do filtro:

            DO PERÍODO   o que vence no recorte escolhido
            DE HOJE      atraso, vencimento de hoje, próximos dias

          Os segundos são POSIÇÃO e não podem seguir o filtro: um atraso de
          março continua sendo atraso com agosto selecionado, e escondê-lo seria
          o pior modo de errar. Mas "posição total" numa legenda de 11px, ao
          lado de um card que segue o filtro, não é aviso — é letra miúda.

          Separados em blocos com título próprio, a pergunta que cada número
          responde fica visível antes de o usuário precisar conferir. */}
      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          No período · {periodLabel(period).toLowerCase()}
        </p>

        <div className="mt-2">
          <Kpi
            label={`${isPayable ? 'A pagar' : 'A receber'} no período`}
            value={d.totals.in_period}
            hint={`${d.totals.in_period_count} título(s) vencendo entre ${formatDate(
              d.period.start ?? d.reference_date,
            )} e ${formatDate(d.period.end)}`}
          />
        </div>
      </div>

      {/*
        ================================================================
        O QUE PEDE AÇÃO — e por que não segue o período
        ================================================================

        Eram QUATRO cartões do mesmo tamanho: total em aberto, vencido, vence
        hoje e próximos 7 dias. Três pediam ação; o primeiro era a posição
        patrimonial — o maior número da tela, e o único que não se faz nada a
        respeito. Ele roubava a atenção dos que importam.

        Agora são dois cartões e uma linha. O total em aberto continua visível,
        em texto, onde não compete com o que precisa ser pago.

        ================================================================
        POR QUE ESTES DOIS IGNORAM O FILTRO
        ================================================================

        O usuário pediu que tudo seguisse o período. Mostrei o efeito nos dados
        reais dele: com "este mês" selecionado, "Vencido" cairia de R$ 3.047,79
        para R$ 22,25 — três contas de julho sumiriam da tela sem aviso.

        Ele escolheu manter independentes. O que muda é o RÓTULO: dizia "não
        segue o filtro", uma desculpa técnica; agora diz que é a situação de
        hoje, seja qual for o período escolhido. A pessoa lê o motivo, e não o
        aviso de uma limitação.
      */}
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Precisa de atenção{' '}
          <span className="font-normal normal-case tracking-normal text-slate-500 dark:text-slate-500">
            · a situação de hoje, seja qual for o período acima
          </span>
        </p>

        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Kpi
            label={isPayable ? 'Vencido' : 'Em atraso'}
            value={d.totals.overdue}
            hint={
              d.totals.overdue_count > 0
                ? `${d.totals.overdue_count} título(s) · de qualquer data`
                : 'nada em atraso'
            }
            alert={d.totals.overdue_count > 0}
          />

          {/*
            "Vence hoje" só aparece quando existe. Um cartão de R$ 0,00 ocupa
            o mesmo espaço de um cheio e não diz nada — e no dia em que houver
            algo vencendo, ele aparece justamente porque mudou.
          */}
          {Number(d.totals.due_today) > 0 ? (
            <Kpi label="Vence hoje" value={d.totals.due_today} alert />
          ) : (
            <Kpi label="Próximos 7 dias" value={d.totals.next_7_days} />
          )}
        </div>

        {Number(d.totals.due_today) > 0 && (
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            Próximos 7 dias: <strong>{formatMoney(d.totals.next_7_days)}</strong>
          </p>
        )}

        {/* A posição patrimonial, em texto: informa sem competir. */}
        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
          Total em aberto, todas as datas:{' '}
          <strong className="text-slate-700 dark:text-slate-300">
            {formatMoney(d.totals.open)}
          </strong>{' '}
          · {d.totals.open_count} título(s)
        </p>
      </div>

      {/* ---------- Indicadores específicos ---------- */}
      {d.essential_split && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-900">
          {/*
            O RÓTULO PRECISA DIZER QUE O VENCIDO ENTRA.

            Dizia só "dos próximos 30 dias", mas o cálculo soma tudo que vence
            até daqui a 30 dias — inclusive o que já está atrasado. Numa base
            real a diferença era R$ 3.025,54, e não havia como reconciliar este
            bloco com o "a vencer em 30 dias" logo acima.

            Incluir o atrasado é o certo: a pergunta é "se o caixa apertar, o
            que não pode deixar de ser pago", e conta vencida não some. Errado
            era o rótulo, não a conta.
          */}
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Vencido + próximos 30 dias
          </p>
          <div className="mt-2 flex flex-wrap gap-5">
            <span>
              <span className="text-slate-500 dark:text-slate-400">Essencial: </span>
              <strong>{formatMoney(d.essential_split.essential)}</strong>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">Demais: </span>
              <strong>{formatMoney(d.essential_split.non_essential)}</strong>
            </span>
            <span className="text-xs text-slate-400">
              se o caixa apertar, o essencial é o que não pode deixar de ser pago
            </span>
          </div>
        </div>
      )}

      {d.receivable_metrics && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-wrap gap-5">
            <span>
              <span className="text-slate-500 dark:text-slate-400">Atraso médio: </span>
              <strong>
                {d.receivable_metrics.average_delay_days === null
                  ? '—'
                  : `${d.receivable_metrics.average_delay_days} dias`}
              </strong>
              <span className="ml-1 text-xs text-slate-400">
                ({d.receivable_metrics.settlements_analyzed} recebimentos)
              </span>
            </span>
            <span>
              <span className="text-slate-500 dark:text-slate-400">Inadimplência: </span>
              <strong
                className={
                  d.receivable_metrics.default_rate_percent > 20
                    ? 'text-rose-600 dark:text-rose-400'
                    : ''
                }
              >
                {d.receivable_metrics.default_rate_percent}%
              </strong>
              <span className="ml-1 text-xs text-slate-400">do que está em aberto</span>
            </span>
          </div>
        </div>
      )}

      {/* ---------- Aging ---------- */}
      <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Composição por prazo
      </h2>
      <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {d.aging.map((a) => (
          <div
            key={a.label}
            className={`rounded-lg border px-3 py-2.5 ${
              a.is_overdue && a.count > 0
                ? 'border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/30'
                : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
            }`}
          >
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{a.label}</p>
            <p className="mt-0.5 font-semibold">{formatMoney(a.total)}</p>
            <p className="text-[11px] text-slate-400">{a.count} título(s)</p>
          </div>
        ))}
      </div>

      {/* ---------- Barra de seleção ---------- */}
      {selected.length > 0 && (
        <div className="sticky top-16 z-20 mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/50">
          <span>
            <strong>{selected.length}</strong> selecionado(s) ·{' '}
            <strong>{formatMoney(selectedTotal.toFixed(4))}</strong>
          </span>
          <button
            onClick={() => setSelected([])}
            className="ml-auto text-xs text-indigo-700 hover:underline dark:text-indigo-300"
          >
            limpar seleção
          </button>
        </div>
      )}

      {/* ---------- Calendário ---------- */}
      <div className="mt-8 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Próximos vencimentos
        </h2>
        <span className="text-xs text-slate-400">
          {d.period.start ? `${formatDate(d.period.start)} a ` : 'até '}
          {formatDate(d.period.end)} · vencidos aparecem sempre, fora do período
        </span>
      </div>

      <div className="mt-2 space-y-3">
        {overdueBucket && (
          <DayGroup
            title="Vencidos"
            total={overdueBucket.total}
            count={overdueBucket.count}
            items={overdueBucket.items}
            tone="danger"
            selected={selected}
            onToggle={(id) =>
              setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
            }
            onSettle={openSettle}
            onPayOther={setPayingOther}
            onOpenDebts={onOpenDebts}
            onOpenCards={onOpenCards}
            onRemove={confirmRemove}
            onClassify={(id, changes) => classify.mutate({ id, changes })}
            isPayable={isPayable}
          />
        )}

        {dated.length === 0 && !overdueBucket && (
          <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            Nada a vencer{' '}
            {d.period.start ? `entre ${formatDate(d.period.start)} e ` : 'até '}
            {formatDate(d.period.end)}.
          </p>
        )}

        {dated.map(([key, bucket]) => (
          <DayGroup
            key={key}
            title={formatDate(bucket.date ?? key)}
            subtitle={weekdayOf(bucket.date ?? key)}
            total={bucket.total}
            count={bucket.count}
            items={bucket.items}
            selected={selected}
            onToggle={(id) =>
              setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
            }
            onSettle={openSettle}
            onPayOther={setPayingOther}
            onOpenDebts={onOpenDebts}
            onOpenCards={onOpenCards}
            onRemove={confirmRemove}
            onClassify={(id, changes) => classify.mutate({ id, changes })}
            isPayable={isPayable}
          />
        ))}
      </div>

      {payingOther && reference.data && (
        <PayFromObligations
          item={{
            kind: payingOther.kind as 'card_invoice' | 'loan_installment',
            id: payingOther.id,
            loan_id: payingOther.loan_id,
          }}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => setPayingOther(null)}
          onPaid={() => {
            setPayingOther(null);
            void queryClient.invalidateQueries({ queryKey: [kind] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      )}

      {settling && reference.data && (
        <SettleDialog
          transaction={settling}
          accounts={reference.data.accounts}
          cards={reference.data.cards}
          transactionEntityId={settling.entity_id}
          selectedIds={selectedIds}
          onOpenAccounts={onOpenAccounts}
          onClose={() => setSettling(null)}
          onSettled={() => {
            setSettling(null);
            setSelected([]);
            void queryClient.invalidateQueries({ queryKey: [kind] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            void queryClient.invalidateQueries({ queryKey: ['transactions'] });
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function DayGroup({
  title,
  subtitle,
  total,
  count,
  items,
  tone,
  selected,
  onToggle,
  onSettle,
  onPayOther,
  onOpenDebts,
  onOpenCards,
  onRemove,
  onClassify,
  isPayable,
}: {
  title: string;
  subtitle?: string;
  total: MoneyString;
  count: number;
  items: Item[];
  tone?: 'danger';
  selected: number[];
  onToggle: (id: number) => void;
  onSettle: (id: number) => void;
  /**
   * Paga fatura ou parcela de dívida SEM sair daqui.
   *
   * Abre o mesmo diálogo das telas de Cartões e Dívidas — as regras (RN-22 e
   * RN-61) moram lá, e duplicá-las aqui seria garantir divergência.
   */
  onPayOther?: (item: Item) => void;
  onOpenDebts?: () => void;
  /** A seta ↗ ainda leva para a tela cheia, para quem quer ver o detalhe. */
  onOpenCards?: () => void;
  onRemove: (item: Item) => void;
  onClassify: (id: number, changes: Record<string, unknown>) => void;
  isPayable: boolean;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border ${
        tone === 'danger'
          ? 'border-rose-200 dark:border-rose-900/50'
          : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      <div
        className={`flex items-center gap-3 px-3.5 py-2 text-sm ${
          tone === 'danger'
            ? 'bg-rose-50 dark:bg-rose-950/30'
            : 'bg-slate-50 dark:bg-slate-800/40'
        }`}
      >
        <span className="font-medium">{title}</span>
        {subtitle && <span className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</span>}
        <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{count} título(s)</span>
        <strong>{formatMoney(total)}</strong>
      </div>

      {/*
        ================================================================
        `table-fixed` + colgroup: as colunas TÊM de ser iguais entre grupos
        ================================================================

        Cada grupo de vencimento é uma tabela própria, e o navegador dimensiona
        as colunas de cada tabela pelo conteúdo DELA. O resultado eram valores
        em posições diferentes a cada bloco — "R$ 22,25" num x, "R$ 1.796,00"
        noutro —, e o olho perde a capacidade de comparar uma coluna de dinheiro
        que não está alinhada.

        Larguras declaradas resolvem sem juntar tudo numa tabela só, que
        obrigaria a transformar os cabeçalhos de data em linhas e perderia a
        borda e o total de cada grupo.
      */}
      <table className="w-full table-fixed bg-white text-sm dark:bg-slate-900">
        <colgroup>
          <col className="w-9" />
          <col className="w-24" />
          <col />
          {/* Cabe "1.250.560,00" sem apertar. */}
          <col className="w-28" />
          {/* ★ + Pagar + ✕, ou "Ver dívida" nas parcelas de dívida. */}
          <col className="w-32" />
        </colgroup>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((i) => (
            <tr key={i.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td className="py-2 pl-3 align-top">
                <input
                  type="checkbox"
                  checked={selected.includes(i.id)}
                  onChange={() => onToggle(i.id)}
                  className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
                />
              </td>
              {/* A DATA DE VENCIMENTO em coluna própria, em toda linha.
                  No grupo "Vencidos" ela é o dado que falta: "35 dias em
                  atraso" não serve para cobrar nem para conferir contra o
                  boleto — a conversa com o cliente é sobre a data. E nos grupos
                  datados ela repete o cabeçalho de propósito, para que a linha
                  continue legível quando alguém imprime ou fotografa a tela. */}
              <td className="whitespace-nowrap py-2 pr-3 align-top">
                <span
                  className={`text-sm tabular-nums ${
                    i.is_overdue ? 'font-medium text-rose-600 dark:text-rose-400' : ''
                  }`}
                >
                  {formatDate(i.due_date)}
                </span>
                {i.days_overdue > 0 && (
                  <span className="block text-[11px] text-rose-500 dark:text-rose-400">
                    {i.days_overdue} dia{i.days_overdue === 1 ? '' : 's'} em atraso
                  </span>
                )}
              </td>
              {/* Única coluna sem largura declarada: fica com o que sobra.
                  Descrição longa quebra em duas linhas em vez de empurrar as
                  colunas — que é o que desalinhava os valores. */}
              <td className="py-2 align-top">
                <span className="font-medium">{i.description}</span>
                {i.is_essential && (
                  <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                    essencial
                  </span>
                )}
                {/*
                  Na fatura, o selo vira VALOR.

                  "Essencial" sozinho seria falso para uma fatura que tem
                  farmácia e restaurante dentro. O número diz a verdade e bate
                  exatamente com o que o bloco dos próximos 30 dias soma.
                */}
                {i.kind === 'card_invoice' && Number(i.essential_amount ?? 0) > 0 && (
                  <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                    {formatMoney(i.essential_amount!)} essenciais
                  </span>
                )}
                <span className="block text-xs text-slate-400">
                  {[i.party_name, i.category_name, i.entity_name].filter(Boolean).join(' · ')}
                </span>
              </td>
              {/*
                ================================================================
                SEM "R$" NA LINHA — a moeda se diz uma vez, no total do grupo
                ================================================================

                Foram três tentativas até chegar aqui, e vale registrar as duas
                que não serviram.
                  1. tudo alinhado à direita, símbolo junto: a borda direita
                     fica certa e a ESQUERDA serrilha, porque "R$ 3.500,00"
                     começa antes de "R$ 240,00". O símbolo pulando de linha em
                     linha é o que o olho lê como desalinhado;
                  2. símbolo preso à esquerda da célula e número à direita: as
                     duas bordas ficam presas e o "R$" fica órfão, separado do
                     próprio valor por um vão do tamanho da coluna.

                O que extrato bancário e planilha fazem: a moeda aparece UMA vez
                — aqui, no total de cada grupo, logo acima — e as linhas trazem
                só o número. Sem símbolo, não há o que serrilhar: sobra uma
                coluna de dígitos alinhada à direita, que é como se lê dinheiro.

                `tabular-nums` é o que faz isso funcionar de verdade: todo
                algarismo ocupa a mesma largura, então "1.111,11" e "8.888,88"
                ocupam o mesmo espaço e as vírgulas caem na mesma vertical.
              */}
              <td className="whitespace-nowrap py-2 pl-2 text-right align-top font-medium tabular-nums">
                {formatAmount(i.remaining_amount)}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-right align-top">
                {isPayable && i.kind === 'transaction' && (
                  <button
                    onClick={() => onClassify(i.id, { is_essential: !i.is_essential })}
                    className="mr-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-amber-600"
                    title={i.is_essential ? 'Desmarcar essencial' : 'Marcar como essencial'}
                  >
                    {i.is_essential ? '★' : '☆'}
                  </button>
                )}
                {/*
                  Parcela de dívida se paga na tela de Dívidas.

                  Lá o pagamento separa principal de juros (RN-61): só os juros
                  viram despesa no DRE, e o principal abate o saldo devedor.
                  Uma baixa comum aqui trataria a parcela inteira como despesa
                  e inflaria o resultado do mês.

                  O botão leva para lá em vez de sumir: sem ele, a linha
                  apareceria na lista sem nenhuma ação possível.
                */}
                {/*
                  A fatura se paga na tela de Cartões.

                  Lá o pagamento vira transferência banco → cartão (RN-22) e
                  liquida as compras dela de uma vez. Uma baixa comum aqui
                  criaria uma despesa nova sobre gastos já reconhecidos e
                  dobraria tudo no resultado do mês.

                  O botão leva para lá em vez de sumir: sem ele, a maior linha
                  da tela ficaria sem nenhuma ação possível.
                */}
                {i.kind === 'card_invoice' || i.kind === 'loan_installment' ? (
                  <>
                    <button
                      onClick={() => onPayOther?.(i)}
                      className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-emerald-500 hover:text-emerald-600 dark:border-slate-700"
                      title={
                        i.kind === 'card_invoice'
                          ? 'Pagar a fatura — vira transferência da conta para o cartão, não despesa nova'
                          : 'Pagar a parcela — separa principal de juros: só o juro é despesa'
                      }
                    >
                      Pagar
                    </button>
                    <button
                      onClick={() => (i.kind === 'card_invoice' ? onOpenCards?.() : onOpenDebts?.())}
                      className="ml-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-indigo-600"
                      title={i.kind === 'card_invoice' ? 'Abrir a fatura' : 'Abrir a dívida'}
                    >
                      ↗
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onSettle(i.id)}
                      className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-emerald-500 hover:text-emerald-600 dark:border-slate-700"
                    >
                      {isPayable ? 'Pagar' : 'Receber'}
                    </button>
                    <button
                      onClick={() => onRemove(i)}
                      className="ml-1.5 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                      title="Excluir título"
                    >
                      ✕
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  alert,
}: {
  label: string;
  value: MoneyString;
  hint?: string;
  alert?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3.5 ${
        alert
          ? 'border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/30'
          : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
      }`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={`mt-1.5 text-base font-semibold ${alert ? 'text-rose-700 dark:text-rose-400' : ''}`}>
        {formatMoney(value)}
      </p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function weekdayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  const days = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
  return days[new Date(y, m - 1, d).getDay()] ?? '';
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse">
      <div className="h-6 w-44 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
      <div className="mt-8 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
